export const CUSTOM_CATEGORY_NAME = "Custom";

export enum BestPracticeCategory {
  Compatibility = "Compatibility",
  MobileResponsiveness = "Mobile Responsiveness",
  CSSPatterns = "CSS Patterns",
  DarkMode = "Dark Mode",
  Accessibility = "Accessibility",
  Structure = "Structure",
}

export enum RuleName {
  // Compatibility
  TableLayoutOnly = "table-layout-only",
  PreserveHtmlAttributes = "preserve-html-attributes",
  PreserveMsoConditionals = "preserve-mso-conditionals",
  ImgAttributes = "img-attributes",
  BgcolorSync = "bgcolor-sync",
  MsoTableSpacingReset = "mso-table-spacing-reset",

  // CSS Patterns
  InlineStyles = "inline-styles",
  PxUnitsOnly = "px-units-only",
  NoShorthand = "no-shorthand",
  PaddingNotMargin = "padding-not-margin",
  NoUnsupportedCss = "no-unsupported-css",
  MsoLineHeightRule = "mso-line-height-rule",
  InlineLinkStyles = "inline-link-styles",
  TextWrapping = "text-wrapping",
  FontStackSyntax = "font-stack-syntax",
  HexColorsOnly = "hex-colors-only",
  RemoveMalformedCss = "remove-malformed-css",

  // Mobile Responsiveness
  MinFontSize = "min-font-size",
  TapTargetSize = "tap-target-size",
  ResponsiveImages = "responsive-images",

  // Dark Mode
  DarkModeMetaTag = "dark-mode-meta-tag",
  DarkModeBackgrounds = "dark-mode-backgrounds",
  DarkModeImages = "dark-mode-images",

  // Accessibility
  HtmlLangAttribute = "html-lang-attribute",
  ImageAltText = "image-alt-text",
  ColorContrast = "color-contrast",
  SemanticHeadings = "semantic-headings",

  // Structure
  SingleColumnLayout = "single-column-layout",
  PreheaderText = "preheader-text",
  DoctypeDeclaration = "doctype-declaration",
  CharacterEncoding = "character-encoding",
}

export enum EmailHtmlRegion {
  DoctypeAndHtml = "DOCTYPE and `<html>` tag",
  Head = "`<head>` section",
  StyleAndMso = "`<style>` blocks and MSO conditionals",
  BodyOpening = "`<body>` opening",
  TablesAndCells = "Tables and `<td>` elements",
  Images = "`<img>` tags",
  Links = "`<a>` tags",
  InlineStyles = "Inline styles throughout",
  TextAndHeadings = "Text and headings",
}

export const RULE_HTML_REGION: Record<RuleName, EmailHtmlRegion> = {
  [RuleName.DoctypeDeclaration]: EmailHtmlRegion.DoctypeAndHtml,
  [RuleName.HtmlLangAttribute]: EmailHtmlRegion.DoctypeAndHtml,

  [RuleName.CharacterEncoding]: EmailHtmlRegion.Head,
  [RuleName.DarkModeMetaTag]: EmailHtmlRegion.Head,

  [RuleName.PreserveMsoConditionals]: EmailHtmlRegion.StyleAndMso,
  [RuleName.RemoveMalformedCss]: EmailHtmlRegion.StyleAndMso,

  [RuleName.PreheaderText]: EmailHtmlRegion.BodyOpening,

  [RuleName.TableLayoutOnly]: EmailHtmlRegion.TablesAndCells,
  [RuleName.PreserveHtmlAttributes]: EmailHtmlRegion.TablesAndCells,
  [RuleName.PaddingNotMargin]: EmailHtmlRegion.TablesAndCells,
  [RuleName.NoUnsupportedCss]: EmailHtmlRegion.TablesAndCells,
  [RuleName.SingleColumnLayout]: EmailHtmlRegion.TablesAndCells,
  [RuleName.BgcolorSync]: EmailHtmlRegion.TablesAndCells,
  [RuleName.MsoTableSpacingReset]: EmailHtmlRegion.TablesAndCells,

  [RuleName.ImgAttributes]: EmailHtmlRegion.Images,
  [RuleName.ImageAltText]: EmailHtmlRegion.Images,
  [RuleName.ResponsiveImages]: EmailHtmlRegion.Images,
  [RuleName.DarkModeImages]: EmailHtmlRegion.Images,

  [RuleName.InlineLinkStyles]: EmailHtmlRegion.Links,

  [RuleName.InlineStyles]: EmailHtmlRegion.InlineStyles,
  [RuleName.PxUnitsOnly]: EmailHtmlRegion.InlineStyles,
  [RuleName.NoShorthand]: EmailHtmlRegion.InlineStyles,
  [RuleName.MsoLineHeightRule]: EmailHtmlRegion.InlineStyles,
  [RuleName.TextWrapping]: EmailHtmlRegion.InlineStyles,
  [RuleName.FontStackSyntax]: EmailHtmlRegion.InlineStyles,
  [RuleName.HexColorsOnly]: EmailHtmlRegion.InlineStyles,

  [RuleName.MinFontSize]: EmailHtmlRegion.TextAndHeadings,
  [RuleName.TapTargetSize]: EmailHtmlRegion.TextAndHeadings,
  [RuleName.ColorContrast]: EmailHtmlRegion.TextAndHeadings,
  [RuleName.SemanticHeadings]: EmailHtmlRegion.TextAndHeadings,
  [RuleName.DarkModeBackgrounds]: EmailHtmlRegion.TextAndHeadings,
};

export interface EmailBestPractice {
  category: BestPracticeCategory;
  name: RuleName;
  rule: string;
}

export const EMAIL_BEST_PRACTICES: EmailBestPractice[] = [
  // ── Compatibility ──
  {
    category: BestPracticeCategory.Compatibility,
    name: RuleName.TableLayoutOnly,
    rule: "Table layout only. Never use <div>-based layout, CSS Grid, or Flexbox — Outlook renders with the Word engine.",
  },
  {
    category: BestPracticeCategory.Compatibility,
    name: RuleName.PreserveHtmlAttributes,
    rule: 'Tables should include cellpadding="0", cellspacing="0", align, valign, role="presentation", and border="0" attributes.',
  },
  {
    category: BestPracticeCategory.Compatibility,
    name: RuleName.PreserveMsoConditionals,
    rule: "Use <!--[if mso]> conditional blocks for Outlook-specific fallbacks (VML backgrounds, rounded buttons, fixed widths). Use <!--[if !mso]><!--> ... <!--<![endif]--> to wrap content that should be hidden from Outlook (e.g. web-font <link> tags, HTML5 elements). Never remove or rewrite either form of conditional comment.",
  },
  {
    category: BestPracticeCategory.Compatibility,
    name: RuleName.ImgAttributes,
    rule: '<img>: always include alt, border="0", and style="display: block" unless the template intentionally uses a border, inline display, or a desktop/mobile swap (paired <img> tags toggled by a media query, where one image has display: none). Never add a non-numeric width or height HTML attribute (e.g. height="auto"). Preserve existing percentage widths. Defer all sizing decisions to ResponsiveImages.',
  },
  {
    category: BestPracticeCategory.Compatibility,
    name: RuleName.BgcolorSync,
    rule: 'Backgrounds: keep bgcolor attributes in sync with background-color CSS — Outlook reads the HTML attribute, not the CSS. For background images, use both the HTML background attribute and style="background-image:url(...)" for maximum client compatibility, and always include a VML fallback in <!--[if mso]> — Outlook will not render CSS background-image alone.',
  },
  {
    category: BestPracticeCategory.Compatibility,
    name: RuleName.MsoTableSpacingReset,
    rule: "Every <table> should include mso-table-lspace: 0pt; mso-table-rspace: 0pt in its inline style. Outlook adds default spacing to the left and right of tables without these resets, creating unexpected gaps between adjacent or nested tables.",
  },

  // ── CSS Patterns ──
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.InlineStyles,
    rule: "Inline all styles on each element. Many email clients strip or ignore <style> blocks.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.PxUnitsOnly,
    rule: "Use px only for all sizing. em, rem, %, vw, vh are unreliable across clients.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.NoShorthand,
    rule: "No CSS shorthand when mixing values — write padding-top/padding-right/padding-bottom/padding-left separately instead of padding shorthand. Same for background → use background-color.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.PaddingNotMargin,
    rule: "Use padding on <td> for spacing, never margin (unreliable in email). For vertical spacing, use padding or spacer rows — <br> renders inconsistently.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.NoUnsupportedCss,
    rule: "Never use: position, float, display: flex, display: grid, min-width, max-height, min-height, calc(), var(), clip-path, filter. Do not use max-width on layout elements (tables, cells, wrappers) except where another enabled rule explicitly requires it (e.g. Single-column layout). max-width and height: auto on <img> are the standard responsive-image pattern and are expected (see ResponsiveImages). Outlook ignores or breaks on the unsupported properties above.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.MsoLineHeightRule,
    rule: "line-height: always pair with mso-line-height-rule: exactly — Outlook overrides line-height without it. Exception: never add `exactly` when an explicit pixel line-height of ≤4px sits on an element containing an <img> (that's a whitespace-control pattern; `exactly` clips the image). If `exactly` is already present in that img-containing ≤4px case, REMOVE the mso-line-height-rule property (keep the line-height) — it clips the image to the line box in Outlook. Intentional 1px spacers/dividers (&nbsp;-only cells with a small explicit height) still get `exactly` and keep it.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.InlineLinkStyles,
    rule: "Links: always inline color and text-decoration on <a> tags. Gmail and Yahoo override link styles with their own defaults if missing.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.TextWrapping,
    rule: "Text wrapping: text-owning elements such as <td>, <p>, <div>, <span>, <a>, and headings must include inline wrapping styles such as overflow-wrap: anywhere; word-break: break-word; so long unbroken text cannot expand the email width in clients.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.FontStackSyntax,
    rule: "Font stacks: never quote CSS generic keywords (sans-serif, serif, monospace, cursive, fantasy, system-ui) — a quoted generic ('sans-serif') is a named-font lookup, not the generic fallback, so spec-compliant clients get no fallback at all, and the quotes escape to &quot;sans-serif&quot; in downstream processing. Quote multi-word family names only (e.g. 'Times New Roman', 'Open Sans'); leave single-word families and generic keywords unquoted.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.HexColorsOnly,
    rule: "Colors: use 6-digit hex (#RRGGBB) for all color values. Convert rgb() to its exact hex equivalent — functional color notation is mishandled by some clients and processing pipelines. Exception: leave rgba() with a real alpha channel in place — hex cannot express the transparency, and flattening it changes the design — but mention any rgba() left in place in your report.",
  },
  {
    category: BestPracticeCategory.CSSPatterns,
    name: RuleName.RemoveMalformedCss,
    rule: "Remove syntactically invalid declarations from <style> blocks: property declarations floating at the root level outside any rule, and declarations fused onto an @import line (keep the @import itself; strip only the fused declaration). Fragile parsers (notably Gmail) can discard an entire <style> block over a single malformed line.",
  },

  // ── Mobile Responsiveness ──
  {
    category: BestPracticeCategory.MobileResponsiveness,
    name: RuleName.MinFontSize,
    rule: "Set font sizes to at least 14px for body text and 22px for headlines. Smaller text triggers auto-zoom on iOS Mail. The floor applies to every font-size declaration, including rules inside @media blocks — mobile tiers that scale text below 14px (e.g. 8-10px at small breakpoints) trigger the same auto-zoom and must be raised. Exception: footer legalese/fine print only (unsubscribe and preference links, copyright lines, mailing address, legal disclaimers) may go as low as 11px, never lower. Never apply this exemption to body copy, headings, or CTAs.",
  },
  {
    category: BestPracticeCategory.MobileResponsiveness,
    name: RuleName.TapTargetSize,
    rule: "CTA buttons should have a minimum tap target of 44x44px with adequate padding. Use padding on the <td> rather than relying on the text size alone.",
  },
  {
    category: BestPracticeCategory.MobileResponsiveness,
    name: RuleName.ResponsiveImages,
    rule: 'Image sizing decision tree — existing sizing intent beats natural dimensions (natural dims give aspect ratio, not display size: an 840×210 logo with height="70" means 280×70, not slot width). Call get_image_dimensions before applying branches 3 or 6. (1) Percentage width attr → preserve, done. (2) Numeric width + height attrs → preserve both (even if they contradict the natural ratio — existing attributes are never edited). (3) Numeric height only → keep it; if natural ratio known via get_image_dimensions, add width = round(height × naturalW / naturalH); otherwise add nothing. (4) Numeric width only → keep it; omit height (Outlook scales proportionally). (5) No attrs, but pixel CSS (style="width:40px") → mirror the pixel value into the HTML width attribute. (6) No attrs/pixel CSS, dimensions known via get_image_dimensions → set HTML width = min(natural, slot), no height, and apply style="width: 100%; max-width: <natural-width>px; height: auto;". (7) No attrs/pixel CSS, dimensions unknown → add slot width only if the img declares width:100% (real fluid intent; max-width:100% alone does not count — icons carry it). Otherwise leave the image alone. Fixed-size icon exemption: never add responsive CSS (width:100%/max-width/height:auto) to fixed-size images (logos, icons, badges, social-media glyphs, or any <img> whose HTML width is a small fixed pixel value ~200px or less, or whose parent <td> has a small fixed pixel width). Attribute completion (branches 1–5) still applies; slot inference (branches 6–7) never does. Adding max-width: 100% to a fixed-size icon is a common regression — webmail clients let the CSS override the HTML width attribute.',
  },

  // ── Dark Mode ──
  {
    category: BestPracticeCategory.DarkMode,
    name: RuleName.DarkModeMetaTag,
    rule: 'Include <meta name="color-scheme" content="light dark"> and <meta name="supported-color-schemes" content="light dark"> in the <head>, plus a <style> block with :root { color-scheme: light dark; } to signal dark mode support to email clients.',
  },
  {
    category: BestPracticeCategory.DarkMode,
    name: RuleName.DarkModeBackgrounds,
    rule: "Always set an explicit background-color on every element that contains text. Without it, dark mode clients may invert the background while leaving text unreadable. Never rely on a transparent or inherited background.",
  },
  {
    category: BestPracticeCategory.DarkMode,
    name: RuleName.DarkModeImages,
    rule: "Avoid transparent PNGs placed on assumed-white backgrounds — dark mode will render the background dark while the image retains transparent areas. Use opaque backgrounds in images or add a white background-color on the containing element.",
  },

  // ── Accessibility ──
  {
    category: BestPracticeCategory.Accessibility,
    name: RuleName.HtmlLangAttribute,
    rule: 'Include a lang attribute on the <html> element (e.g. lang="en") so screen readers announce content in the correct language.',
  },
  {
    category: BestPracticeCategory.Accessibility,
    name: RuleName.ImageAltText,
    rule: 'Every <img> must have a meaningful alt attribute describing the image content. Decorative images should use alt="" (empty) so screen readers skip them. Never omit alt entirely.',
  },
  {
    category: BestPracticeCategory.Accessibility,
    name: RuleName.ColorContrast,
    rule: "Text must have a minimum contrast ratio of 4.5:1 against its background for normal text and 3:1 for large text (18px+ or 14px+ bold) per WCAG AA. Avoid light gray text on white or similar low-contrast combinations.",
  },
  {
    category: BestPracticeCategory.Accessibility,
    name: RuleName.SemanticHeadings,
    rule: "Use heading tags (<h1>, <h2>, <h3>) in a logical hierarchy for email headings rather than styling <td> or <span> elements to look like headings. This helps screen readers navigate the email structure.",
  },

  // ── Structure ──
  {
    category: BestPracticeCategory.Structure,
    name: RuleName.SingleColumnLayout,
    rule: "Use a single-column layout with the main content table capped at 600-680px max width and centered with margin: 0 auto. This is the standard email viewport width across desktop clients (Gmail, Outlook, Yahoo, Apple Mail) — wider tables cause clipping or unexpected scaling. Do not cap a full-width canvas/background wrapper table; cap the nested content table instead.",
  },
  {
    category: BestPracticeCategory.Structure,
    name: RuleName.PreheaderText,
    rule: 'Include hidden preheader text immediately after the <body> tag using a <span> or <div> with style="display: none; max-height: 0; overflow: hidden; mso-hide: all;". This controls the preview text shown in inbox listings. Without it, clients pull the first visible text, which is often a "View in browser" link or navigation.',
  },
  {
    category: BestPracticeCategory.Structure,
    name: RuleName.DoctypeDeclaration,
    rule: "Include <!DOCTYPE html> at the top of the email. Missing or incorrect doctypes trigger quirks mode in some email clients, causing inconsistent rendering of spacing, box model, and table layouts.",
  },
  {
    category: BestPracticeCategory.Structure,
    name: RuleName.CharacterEncoding,
    rule: 'Include <meta charset="UTF-8"> in the <head> to ensure special characters, accented letters, and emoji render correctly across all email clients. Without it, clients may fall back to a different encoding and display garbled text.',
  },
];

// ── Best Practice Run Types ──

export enum BestPracticeRunStatus {
  Pending = "pending",
  Completed = "completed",
  Failed = "failed",
}

export interface BestPracticeAnalysisItem {
  category: BestPracticeCategory | typeof CUSTOM_CATEGORY_NAME;
  name: RuleName | string;
}

export interface BestPracticeFixItem extends BestPracticeAnalysisItem {
  fix: string;
}

export interface BestPracticeAnalysisOutput {
  adhered: BestPracticeAnalysisItem[];
  fixed: BestPracticeFixItem[];
}

export interface BestPracticeRunInput {
  rules: Array<{ category: string; name: string; rule: string }>;
}

export interface BestPracticeRun {
  id: string;
  workspaceId: number;
  artifactId: string;
  artifactVersionId: string | null;
  llmChatSessionId: string | null;
  status: BestPracticeRunStatus;
  appliedToArtifactVersionId: string | null;
  input: BestPracticeRunInput | null;
  output: BestPracticeAnalysisOutput | null;
  createdAt: string;
  updatedAt: string;
}
